import { isEmpty } from 'lodash';
import { Controller, useForm } from 'react-hook-form';
import { SimpleDialog } from 'src/components/modal';
import type { ModalProps } from 'src/components/modal/Modal.interface';
import { Snackbar } from 'src/components/snackbar';
import { InputWidget } from 'src/components/widget';
import { useCreateProductVariationSize } from 'src/hooks';
import { lang } from 'src/utils';

interface CreateVariationSizeModelProps extends Omit<ModalProps, 'children'> {}
interface FormValues {
  name: string;
  short: string;
}

export const CreateVariationSizeModel = (
  props: CreateVariationSizeModelProps,
) => {
  const createProductVariationSize = useCreateProductVariationSize();

  const form = useForm({
    defaultValues: { name: '', short: '' },
    mode: 'onChange',
  });

  const isNameEmpty = isEmpty(form.watch('name'));

  const handleSubmitData = async (data: FormValues) => {
    try {
      await createProductVariationSize.mutateAsync(data);

      Snackbar.success(`${data.name} ${lang.successfullyAdded}`);

      props.onClose();
    } catch (error: any) {
      switch (error?.response?.data?.error) {
        case lang.errors.duplicateItem: {
          form.setError('name', {
            message: lang.errors.duplicatedName,
          });
          break;
        }
        default: {
          Snackbar.error(error?.response?.data?.error ?? error?.message);
          break;
        }
      }
    }
  };

  return (
    <SimpleDialog
      {...props}
      modalSize="sm"
      title={lang.variationSizeModelTitle}
      confirmText={lang.confirm}
      cancelButtonColor="error"
      disabled={!isEmpty(form.formState.errors) || isNameEmpty}
      isLoading={createProductVariationSize.isLoading}
      onClickCancel={props.onClose}
      onClickConfirm={form.handleSubmit(handleSubmitData)}>
      <Controller
        name="name"
        control={form.control}
        rules={{
          required: '',
          maxLength: {
            value: 100,
            message: lang.errors.maxLength(100),
          },
        }}
        render={({ field, fieldState }) => {
          return (
            <InputWidget
              {...field}
              label={lang.variationName}
              hasError={!!fieldState.error?.message}
              error={fieldState.error?.message}
            />
          );
        }}
      />

      <Controller
        name="short"
        control={form.control}
        rules={{
          maxLength: {
            value: 10,
            message: lang.errors.maxLength(10),
          },
        }}
        render={({ field, fieldState }) => {
          return (
            <InputWidget
              {...field}
              label={lang.variationAcronym}
              hasError={!!fieldState.error?.message}
              error={fieldState.error?.message}
            />
          );
        }}
      />
    </SimpleDialog>
  );
};
